import { Slot } from '@radix-ui/react-slot';
import { useFocusWithin } from 'v5/platform/react/useFocusWithin';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ComponentProps, FocusEvent } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { hideNumberInputSpinnersClassName } from 'v5/platform/dom/hideNumberInputSpinnersClassName';
import {
  findFocusableElement,
  getFocusableSelector
} from '../platform/dom/isFocusable';
import { Overwrite } from '../platform/typescript';
import { focusRings } from './_common/focusRing';

/**
 * @deprecated Use {@link inputGroup2Variants} from `./InputGroup2` instead.
 * Map `variant` (`regular` | `inline` | `plain`) to `tone`; use `variant` for semantic color (`default` | `danger`).
 */
export const inputGroupVariants = cva(
  [
    'overflow-hidden', // for borders and radius to work
    'group',
    'cursor-text aria-readonly:cursor-default',
    'flex w-full',
    'file:border-0 file:bg-transparent file:text-base file:font-medium file:text-fg-default',
    'placeholder:text-fg-muted',
    'disabled:cursor-not-allowed disabled:opacity-50',
    '[&_[data-element=input-group-slot]]:px-3'
  ],
  {
    variants: {
      size: {
        xs: 'text-base min-h-[20px] [&_svg]:size-3 [&_[data-element=input-group-button]]:px-1.5 [&_[data-element=input-group-button]]:min-h-[20px]',
        sm: 'text-base min-h-9 [&_svg]:size-4 [&_[data-element=input-group-button]]:px-2.5 [&_[data-element=input-group-button]]:min-h-9',
        md: 'text-base min-h-12 [&_svg]:size-5 [&_[data-element=input-group-button]]:px-3.5 [&_[data-element=input-group-button]]:min-h-12',
        lg: 'text-lg min-h-15 [&_svg]:size-6 [&_[data-element=input-group-button]]:px-5 [&_[data-element=input-group-button]]:min-h-15',
        xl: 'text-xl min-h-17 [&_svg]:size-6 [&_[data-element=input-group-button]]:px-5 [&_[data-element=input-group-button]]:min-h-17'
      },
      variant: {
        regular: [
          'rounded',
          'border border-border-default bg-bg-default text-fg-default',
          'not-aria-readonly:hocus:border-bg-inset',
          '[&_input]:first:pl-2 [&_input]:last:pr-2',
          '[&_[data-element="input-group-input"]]:first:pl-2 [&_[data-element="input-group-input"]]:last:pr-2',
          focusRings.focusWithin.normal,
          focusRings.focusWithin.invalidFocused
        ],
        inline: [
          'transition-all',
          'text-fg-default',
          'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_] aria-expanded:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-border-default',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:has-focus:shadow-brand-300',
          'not-aria-readonly:hover:focus-within:shadow-brand-400 aria-expanded:shadow-brand-400'
        ],
        plain: [
          'transition-all',
          'text-fg-default',
          'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_] aria-expanded:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-transparent',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:has-focus:shadow-brand-300',
          'not-aria-readonly:hover:focus-within:shadow-brand-400 aria-expanded:shadow-brand-400'
        ]
      }
    },
    defaultVariants: {
      variant: 'regular',
      size: 'md'
    }
  }
);

/**
 * @deprecated Use {@link InputGroup2Props} from `./InputGroup2` instead.
 */
export type InputGroupProps = Overwrite<
  ComponentProps<'div'>,
  VariantProps<typeof inputGroupVariants> & {
    leftSlot?: React.ReactNode;
    rightSlot?: React.ReactNode;
    onBlur?: (e: FocusEvent) => void;
    onFocus?: (e: FocusEvent) => void;
  }
>;

/** @deprecated Use {@link Input2Tone} from `./Input2` instead (`regular` | `inline` | `plain`). */
export type InputGroupVariant = InputGroupProps['variant'];
export type InputGroupSize = InputGroupProps['size'];

/**
 * @deprecated Use {@link InputGroup2} from `./InputGroup2` instead.
 */
export const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  (
    {
      leftSlot,
      rightSlot,
      children,
      className,
      variant,
      size,
      onFocus,
      onBlur,
      ...props
    },
    ref
  ) => {
    const { focusWithinProps } = useFocusWithin({
      onFocusWithin: onFocus,
      onBlurWithin: onBlur
    });

    return (
      <Slot {...focusWithinProps}>
        <div
          className={cn(inputGroupVariants({ variant, size }), className)}
          ref={ref}
          onMouseDownCapture={e => {
            if (
              e.target instanceof Element &&
              e.target.closest(getFocusableSelector())
            ) {
              return;
            }
            findFocusableElement(e.currentTarget)?.focus();
            e.stopPropagation();
            e.preventDefault();
          }}
          {...props}
          {...focusWithinProps}
        >
          {leftSlot}
          {children}
          {rightSlot}
        </div>
      </Slot>
    );
  }
);
InputGroup.displayName = 'InputGroup';

/** @deprecated Use {@link InputGroup2Input} from `./InputGroup2` instead. */
export type InputGroupInputProps = React.ComponentProps<'input'> & {
  highlightOnFocus?: boolean;
  asChild?: boolean;
};

/**
 * @deprecated Use {@link InputGroup2Input} from `./InputGroup2` instead.
 */
export const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  InputGroupInputProps
>(
  (
    { className, onFocus, readOnly, highlightOnFocus, asChild, type, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'input';

    return (
      <Comp
        data-element="input-group-input"
        tabIndex={readOnly ? -1 : props.tabIndex}
        className={cn(
          'w-full border-none bg-transparent outline-none placeholder:text-fg-muted',
          !asChild && type === 'number' && hideNumberInputSpinnersClassName,
          className
        )}
        ref={ref}
        readOnly={readOnly}
        aria-readonly={readOnly}
        {...(!asChild ? { type } : {})}
        {...props}
        onFocus={e => {
          if (highlightOnFocus) {
            e.target.select();
          }
          onFocus?.(e);
        }}
      />
    );
  }
);
InputGroupInput.displayName = 'InputGroupInput';

/**
 * @deprecated Use {@link InputGroup2Slot} from `./InputGroup2` instead.
 */
export const InputGroupSlot = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      data-element="input-group-slot"
      className={cn('flex items-center', className)}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  );
});

/**
 * @deprecated Use {@link InputGroup2Button} from `./InputGroup2` instead.
 */
export const InputGroupButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, children, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-element="input-group-button"
      type="button"
      className={cn(
        'flex items-center not-disabled:xhover:bg-brand-900/5 focus-visible:bg-brand-900/7 transition-colors',
        className
      )}
      ref={ref}
      {...props}
    >
      {children}
    </Comp>
  );
});
